import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { getStationKey, getDeviceId, clearStation } from '../lib/posStation';
import { isNativePrintAvailable, printDocketNative, printDocketsNative } from '../lib/nativePrint';
import './PosPage.css';

// POS-scoped settings page. Counter staff can edit kitchen printer IP/port,
// station name and terminal ID for the station this iPad is bound to,
// without needing an admin PIN. Cafe stickers are printed by the on-site
// print agent (Admin → Settings for the agent token). All writes go to
// the pos_stations row for the current station via ?station=<key> on
// /api/pos/config — one iPad's edits can no longer clobber another.
export default function PosSettingsPage() {
  const navigate = useNavigate();
  const stationKey = getStationKey();
  const [config, setConfig] = useState({
    counter_printer_ip: '',
    counter_printer_port: '9100',
    counter_station_name: 'Station 1',
    counter_tyro_terminal_id: '',
    cafe_pending_jobs: 0,
    eftpos_provider: 'manual',
    kitchen_eod_discount: false,
    station: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingCafe, setTestingCafe] = useState(false);
  const [printingOnline, setPrintingOnline] = useState(false);
  const [status, setStatus] = useState(null); // { ok: boolean, message: string }

  // Clear the /pos body scroll lock so this page can scroll on iPad
  // (PosPage adds pos-locked; navigation sometimes leaves it behind).
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.documentElement.classList.remove('pos-locked');
    document.body.classList.remove('pos-locked');
    return undefined;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      if (!stationKey) {
        setLoading(false);
        return;
      }
      api.getPosConfig(stationKey)
        .then((cfg) => {
          if (cancelled) return;
          setConfig((prev) => ({ ...prev, ...cfg }));
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setStatus({ ok: false, message: err.message });
          setLoading(false);
        });
    };
    load();
    // Admin flips eftpos_provider on another device — refresh when this
    // settings page comes back into view.
    const onFocus = () => load();
    const onVis = () => {
      if (document.visibilityState === 'visible') load();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [stationKey]);

  const handleChangeStation = async () => {
    if (!window.confirm('Sign out of this station and pick again on the next screen?')) return;
    try {
      if (stationKey) await api.releasePosStation(stationKey, getDeviceId());
    } catch {
      /* still leave locally so this iPad can pick another till */
    }
    clearStation();
    navigate('/pos');
  };

  const update = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setStatus(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await api.updatePosConfig({
        counter_printer_ip: config.counter_printer_ip.trim(),
        counter_printer_port: config.counter_printer_port.trim() || '9100',
        counter_station_name: config.counter_station_name.trim() || 'Station 1',
        counter_tyro_terminal_id: (config.counter_tyro_terminal_id || '').trim(),
      }, stationKey);
      setStatus({ ok: true, message: 'Settings saved.' });
    } catch (err) {
      setStatus({ ok: false, message: err.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setStatus(null);
    try {
      const ip = config.counter_printer_ip.trim();
      const port = config.counter_printer_port.trim() || '9100';
      if (!ip) {
        setStatus({ ok: false, message: 'Enter a counter printer IP first.' });
        setTesting(false);
        return;
      }
      const native = isNativePrintAvailable();
      // Native pos-app: ask the server to *build* the test docket
      // bytes, then push them over the LAN ourselves via TcpPrint.
      // Server-side push has never worked from Azure to the church
      // LAN — the native round-trip is the only path that actually
      // proves the wiring end-to-end.
      const result = await api.testPrinter(ip, port, config.counter_station_name, { clientPrints: native });
      if (native && result?.docket) {
        await printDocketNative(result.docket);
        setStatus({ ok: true, message: `Test docket printed on ${ip}:${port}.` });
      } else {
        setStatus({
          ok: true,
          message: native
            ? `Test docket sent to ${ip}:${port}. (Server did not return a payload — check config.)`
            : `Test docket dispatched via server to ${ip}:${port}. (This will only reach the printer if the server can route to the LAN — in production the native POS app is required.)`,
        });
      }
    } catch (err) {
      setStatus({ ok: false, message: `Test failed: ${err.message}` });
    } finally {
      setTesting(false);
    }
  };

  const handleTestCafe = async () => {
    setTestingCafe(true);
    setStatus(null);
    try {
      const result = await api.testCafePrinter();
      setStatus({
        ok: true,
        message: `Test sticker queued (job #${result.job_id || '—'}). It prints when the cafe print agent is running.`,
      });
      setConfig((prev) => ({
        ...prev,
        cafe_pending_jobs: (prev.cafe_pending_jobs || 0) + (result.queued || 1),
      }));
    } catch (err) {
      setStatus({ ok: false, message: `Cafe test failed: ${err.message}` });
    } finally {
      setTestingCafe(false);
    }
  };

  const handlePrintOnlineKitchen = async () => {
    if (!window.confirm(
      'Print thermal dockets for ALL outstanding kitchen orders this service day?\n\nSlips are sorted St Barbara then St Noufer — tear and hand each pile to the right kitchen. Each slip shows order # and customer name.'
    )) return;
    setPrintingOnline(true);
    setStatus(null);
    try {
      const clientPrints = isNativePrintAvailable();
      const result = await api.printOutstandingKitchenDockets({ clientPrints });
      if (clientPrints && result.dockets?.length) {
        await printDocketsNative(result.dockets);
        setStatus({
          ok: true,
          message: `Printed ${result.dockets.length} docket(s) for ${result.total} order(s).`,
        });
      } else {
        setStatus({ ok: true, message: result.message || 'Print requested.' });
      }
    } catch (err) {
      setStatus({ ok: false, message: err.message || 'Print failed.' });
    } finally {
      setPrintingOnline(false);
    }
  };

  const busy = saving || testing || testingCafe || printingOnline;

  return (
    <div className="pos-settings-page">
      <div className="pos-settings-wrap">
        <h1 className="pos-settings-title">POS settings</h1>
        <div className="pos-settings-subtitle">
          Counter station and kitchen printer setup. Cafe stickers use the print agent on the cafe PC (Admin → Settings).
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            background: '#f9f5ea',
            border: '1px solid #e2d8c6',
            borderRadius: 8,
            margin: '12px 0 20px',
          }}
        >
          <div>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b5a3d' }}>
              This iPad is bound to
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#2b2113' }}>
              {config.station?.name || config.counter_station_name || '—'}
              {stationKey && (
                <span style={{ fontSize: 12, fontWeight: 400, color: '#6b5a3d', marginLeft: 8 }}>
                  ({stationKey})
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="pos-modal-btn pos-modal-btn-secondary"
            onClick={handleChangeStation}
          >
            Change station
          </button>
        </div>

        {loading ? (
          <div>Loading…</div>
        ) : (
          <>
            <div className="pos-settings-field">
              <label className="pos-settings-label" htmlFor="pos-eod-discount">
                End of day discount
              </label>
              <label
                htmlFor="pos-eod-discount"
                style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 16, fontWeight: 600 }}
              >
                <input
                  id="pos-eod-discount"
                  type="checkbox"
                  checked={!!config.kitchen_eod_discount}
                  onChange={async (e) => {
                    const on = e.target.checked;
                    update('kitchen_eod_discount', on);
                    try {
                      await api.updatePosConfig({ kitchen_eod_discount: on }, stationKey);
                      setStatus({
                        ok: true,
                        message: on
                          ? 'Kitchen 50% off is on for every POS station.'
                          : 'Kitchen prices are back to normal.',
                      });
                    } catch (err) {
                      update('kitchen_eod_discount', !on);
                      setStatus({ ok: false, message: err.message || 'Could not update discount.' });
                    }
                  }}
                />
                Kitchen items 50% off
              </label>
              <div className="pos-settings-help">
                Shop-wide. Cafe and Orban stay full price. Admin catalog prices are not changed.
                Applies as soon as you tap — no Save needed.
              </div>
            </div>

            <div className="pos-settings-field">
              <label className="pos-settings-label" htmlFor="pos-station">
                Station name
              </label>
              <input
                id="pos-station"
                className="pos-settings-input"
                type="text"
                value={config.counter_station_name}
                onChange={(e) => update('counter_station_name', e.target.value)}
                placeholder="Counter #1"
              />
              <div className="pos-settings-help">
                Printed on every kitchen docket so runners know which counter to bring change to.
              </div>
            </div>

            <h2 className="pos-settings-title" style={{ fontSize: '1.1rem', marginTop: 28 }}>
              Kitchen docket printer
            </h2>

            <div className="pos-settings-field">
              <label className="pos-settings-label" htmlFor="pos-printer-ip">
                Printer IP address
              </label>
              <input
                id="pos-printer-ip"
                className="pos-settings-input"
                type="text"
                value={config.counter_printer_ip}
                onChange={(e) => update('counter_printer_ip', e.target.value)}
                placeholder="192.168.0.50"
                inputMode="decimal"
              />
              <div className="pos-settings-help">
                Static LAN IP of the 80&nbsp;mm ESC/POS printer (e.g. Epson TM-T20III, Bixolon SRP-350).
                Kitchen orders only — cafe stickers go through the cafe PC agent.
              </div>
            </div>

            <div className="pos-settings-field">
              <label className="pos-settings-label" htmlFor="pos-printer-port">
                Printer port
              </label>
              <input
                id="pos-printer-port"
                className="pos-settings-input"
                type="text"
                value={config.counter_printer_port}
                onChange={(e) => update('counter_printer_port', e.target.value)}
                placeholder="9100"
                inputMode="numeric"
              />
              <div className="pos-settings-help">
                Most network thermal printers listen on port <code>9100</code> (raw / RAW ESC/POS).
              </div>
            </div>

            <div className="pos-settings-field">
              <label className="pos-settings-label">
                Online kitchen orders
              </label>
              <button
                type="button"
                className="pos-modal-btn pos-modal-btn-secondary"
                disabled={busy}
                title="Print all outstanding online/counter kitchen dockets for this service day"
                onClick={handlePrintOnlineKitchen}
              >
                {printingOnline ? 'Printing…' : 'Print Online Kitchen Orders (Saturday)'}
              </button>
              <div className="pos-settings-help">
                Saturday / service-day dump: thermal slips for outstanding app and counter kitchen orders, sorted St Barbara then St Noufer. Does not print cafe stickers.
              </div>
            </div>

            <h2 className="pos-settings-title" style={{ fontSize: '1.1rem', marginTop: 28 }}>
              Cafe stickers (Zebra Sticker Printer)
            </h2>
            <div className="pos-settings-help" style={{ marginBottom: 12 }}>
              Stickers queue in Azure and print on the cafe PC via the print agent.
              Set the agent token and Zebra IP on that PC — see <code>print-agent/README.md</code>.
              {typeof config.cafe_pending_jobs === 'number' && (
                <> Pending in queue: <strong>{config.cafe_pending_jobs}</strong>.</>
              )}
            </div>

            <h2 className="pos-settings-title" style={{ fontSize: '1.1rem', marginTop: 28 }}>
              EFTPOS terminal (Tyro Pay Instore)
            </h2>

            <div className="pos-settings-field">
              <label className="pos-settings-label" htmlFor="pos-tyro-tid">
                Paired Tyro terminal ID (TID)
              </label>
              <input
                id="pos-tyro-tid"
                className="pos-settings-input"
                type="text"
                value={config.counter_tyro_terminal_id || ''}
                onChange={(e) => update('counter_tyro_terminal_id', e.target.value)}
                placeholder="e.g. 12345678"
                inputMode="numeric"
              />
              <div className="pos-settings-help">
                The DX8000 (Tyro Pro) this station is paired to. Every card
                sale from this iPad routes to this terminal. Leave blank to
                fall back to the merchant default (Admin → Payments →
                Default Terminal ID).
              </div>
            </div>

            <div className="pos-settings-field">
              <label className="pos-settings-label">EFTPOS provider</label>
              <input
                className="pos-settings-input"
                type="text"
                value={
                  config.eftpos_provider === 'tyro_pay_instore'
                    ? 'Tyro Pay Instore (integrated via HTTPS)'
                    : config.eftpos_provider === 'tyro'
                    ? 'Tyro iClient (browser SDK — deprecated for iPad)'
                    : 'Manual (staff types amount on DX8000)'
                }
                readOnly
                style={{ background: '#f9fafb' }}
              />
              <div className="pos-settings-help">
                Change on Admin → Payments. This screen refreshes when you
                return to it; if it still says Manual after saving, force-quit
                the POS app and reopen.
              </div>
            </div>

            {status && (
              <div className={`pos-settings-status ${status.ok ? 'pos-settings-status-ok' : 'pos-settings-status-err'}`}>
                {status.message}
              </div>
            )}

            <div className="pos-settings-actions">
              <Link to="/pos" className="pos-modal-btn pos-modal-btn-secondary">
                Back to POS
              </Link>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  className="pos-modal-btn pos-modal-btn-secondary"
                  onClick={handleTest}
                  disabled={busy}
                >
                  {testing ? 'Testing…' : 'Test kitchen print'}
                </button>
                <button
                  className="pos-modal-btn pos-modal-btn-secondary"
                  onClick={handleTestCafe}
                  disabled={busy}
                >
                  {testingCafe ? 'Queuing…' : 'Queue cafe test sticker'}
                </button>
                <button
                  className="pos-modal-btn pos-modal-btn-primary"
                  onClick={handleSave}
                  disabled={busy}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
